import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { t } from "@/i18n-js/instance";
import { communityPlanAddonApi } from "@/react/api/communityPlanAddonApi";
import type { AddOnQuantity, PlanAddOn } from "@circle-react/types";
import { useCommunityPlanAddonApi } from "./hooks/useCommunityPlanAddonApi";
import { calculateAddOnPrice } from "./utils";

interface AddOnsTotalPrices {
  [key: string]: number;
}
interface DefaultValues {
  [key: string]: AddOnQuantity;
}

interface AddOnWithModelProps extends PlanAddOn {
  step: number;
}

interface AddOnsSettingsContextType {
  addOnsData: AddOnWithModelProps[];
  defaultValues: DefaultValues | undefined;
  getAddOnPrice: (name: string, quantity: AddOnQuantity) => number;
  getPlanCurrency: () => string;
  getPlanInterval: () => string;
  grandfatheredAddonNames: string | undefined;
  handleAddonsMutation: {
    mutate: (params: Record<string, AddOnQuantity>) => void;
    isLoading: boolean;
  };
  isLoading: boolean;
  onSubmit: (data: DefaultValues) => void;
  serverErrors: { title: string } | undefined;
  total: number | undefined;
  updateTotalPrice: (name: string, price: number) => void;
}

interface AddOnsSettingsContextProviderProps {
  children: ReactNode;
  onCloseModal?: () => void;
  onSuccess?: () => void;
  refetchCurrentPlan?: () => void;
}

const AddOnsSettingsContext = createContext<
  AddOnsSettingsContextType | undefined
>(undefined);
AddOnsSettingsContext.displayName = "AddOnsSettingsContext";

export const AddOnsSettingsContextProvider = ({
  onSuccess,
  onCloseModal,
  children,
  refetchCurrentPlan,
}: AddOnsSettingsContextProviderProps) => {
  const [total, setTotal] = useState<number>();
  const [addOnsData, setAddOnsData] = useState<AddOnWithModelProps[]>([]);
  const [defaultValues, setDefaultValues] = useState<DefaultValues>();
  const [addOnsTotalPrices, setAddOnsTotalPrices] =
    useState<AddOnsTotalPrices>();
  const [isLoading, setIsLoading] = useState(true);
  const [serverErrors, setServerErrors] = useState<{ title: string }>();
  const [grandfatheredAddonNames, setGrandfatheredAddonNames] =
    useState<string>();

  const { handleAddonsMutation } = useCommunityPlanAddonApi({
    handleOnSuccess: onSuccess,
    setServerErrors: setServerErrors,
    onCloseModal: onCloseModal,
    refetchCurrentPlan: refetchCurrentPlan,
  });

  const updateTotalPrice = (name: string, price: number): void => {
    const state = { ...addOnsTotalPrices };
    state[name] = price;
    setAddOnsTotalPrices(state);
  };

  const calculateTotal = (): void => {
    let total = 0;
    if (!addOnsTotalPrices) return;
    Object.keys(addOnsTotalPrices).forEach(name => {
      total += addOnsTotalPrices[name];
    });
    setTotal(total);
  };

  const buildFormModel = (data: PlanAddOn[]): AddOnWithModelProps[] =>
    data.map(item => ({
      ...item,
      step: item.unit_size,
    }));

  const buildPricesModel = (data: PlanAddOn[]): AddOnsTotalPrices => {
    const obj: AddOnsTotalPrices = {};
    data.forEach(item => {
      obj[item.slug] = calculateAddOnPrice(
        item.quantity,
        item.base_amount,
        item.unit_size,
      );
    });
    return obj;
  };

  const getGrandfatheredAddonNames = (data: PlanAddOn[]): string =>
    data
      .filter(planAddon => planAddon.is_grandfathered)
      .map(planAddon => planAddon.name)
      .join(", ");

  const buildDefaultValues = (data: PlanAddOn[]): DefaultValues => {
    const values: DefaultValues = {};
    data.forEach(item => {
      values[item.slug] = item.quantity;
    });

    return values;
  };

  const getAddOnBySlug = (slug: string): AddOnWithModelProps | undefined =>
    addOnsData.find(item => item.slug === slug);

  const getPlanInterval = (): string => addOnsData[0].interval;

  const getPlanCurrency = (): string => addOnsData[0].price_currency;

  const getAddOnPrice = (name: string, quantity: number | boolean): number => {
    const addOn = getAddOnBySlug(name);

    if (!addOn) return 0;

    return calculateAddOnPrice(quantity, addOn.base_amount, addOn.unit_size);
  };

  const loadData = async (): Promise<void> => {
    try {
      const planAddons = await communityPlanAddonApi.index();

      setDefaultValues(buildDefaultValues(planAddons));
      setAddOnsData(buildFormModel(planAddons));
      setGrandfatheredAddonNames(getGrandfatheredAddonNames(planAddons));
      setAddOnsTotalPrices(buildPricesModel(planAddons));
    } catch {
      setServerErrors({ title: t("something_went_wrong") });
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = (data: DefaultValues) => {
    const params: Record<string, number | boolean> = {};
    Object.keys(data).forEach(key => {
      const addOn = getAddOnBySlug(key);
      if (addOn) {
        params[addOn.id.toString()] = data[key];
      }
    });

    handleAddonsMutation.mutate(params);
  };

  useEffect(() => {
    void loadData();
  }, []);

  useEffect(() => {
    calculateTotal();
  }, [addOnsTotalPrices]);

  const value: AddOnsSettingsContextType = {
    getPlanInterval,
    getPlanCurrency,
    total,
    addOnsData,
    defaultValues,
    isLoading,
    handleAddonsMutation,
    serverErrors,
    getAddOnPrice,
    updateTotalPrice,
    onSubmit,
    grandfatheredAddonNames,
  };

  return (
    <AddOnsSettingsContext.Provider value={value}>
      {children}
    </AddOnsSettingsContext.Provider>
  );
};

export const useAddOnsSettingsContext = (): AddOnsSettingsContextType => {
  const context = useContext(AddOnsSettingsContext);
  if (context === undefined) {
    throw new Error(
      "useAddOnsSettingsContext must be used within an AddOnsSettingsContextProvider",
    );
  }
  return context;
};
